import { useEffect, useRef } from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { StepProps, useAutoNavigate } from '@betterment/js-runtime';
import { Banner, Box, H1, Text } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';

import { trackClick } from '../../features/track';
import { AdvisorNetworkIntentStatus } from '../../graphql/schema';
import { WorkflowLayout } from '../../Shared/layout/WorkflowLayout';
import { urlBuilder } from '../../Shared/urlBuilder';
import { FinancialServiceOptionCard } from '../components/FinancialServiceOptionCard';
import { TransferSupport } from '../components/TransferSupport';
import { FinancialProfileDetailsDocument } from '../graphql/query.financialProfileDetails.generated';
import { FinancialServiceOptionsDocument } from '../graphql/query.financialServiceOptions.generated';

function AdvisorNetworkIntentStatusCopy(status?: AdvisorNetworkIntentStatus) {
  switch (status) {
    case AdvisorNetworkIntentStatus.Interested:
      return 'Your advisor match call is booked. Check your email to view or update it.';
    case AdvisorNetworkIntentStatus.Engaged:
    case AdvisorNetworkIntentStatus.Invited:
      return "Your advisor matches are on the way. You'll get an email soon to review them and book your intro calls.";
    case AdvisorNetworkIntentStatus.Canceled:
    default:
      return null;
  }
}

export const PlanSelectionStep: React.FC<StepProps> = ({ flowState, goToNextStep }) => {
  const navigate = useAutoNavigate();
  const hasRedirected = useRef(false);

  const { data: financialProfileData, loading: isFinancialProfileLoading } = useGraphQLQuery(
    FinancialProfileDetailsDocument,
    {
      throwOnError: true,
      fetchPolicy: 'network-only',
    }
  );

  const { data } = useGraphQLQuery(FinancialServiceOptionsDocument, {
    throwOnError: true,
  });

  useEffect(() => {
    if (!isFinancialProfileLoading && !financialProfileData?.financialProfile && !hasRedirected.current) {
      hasRedirected.current = true;
      const fullPath = window.location.pathname;
      void navigate(
        urlBuilder.appProfileSetupFlow.absolute({
          return_to: fullPath,
        })
      );
    }
  }, [financialProfileData, isFinancialProfileLoading, navigate]);

  if (isFinancialProfileLoading) return null;

  if (!financialProfileData?.financialProfile) {
    return null;
  }

  if (!data?.financialServiceOptions.length) return null;

  const { financialServiceOptions, user } = data;

  const isExpired = user.advisorNetworkIntent?.isExpired;
  const statusCopy = AdvisorNetworkIntentStatusCopy(user.advisorNetworkIntent?.status);
  const willShowBanner = statusCopy && !isExpired;

  return (
    <WorkflowLayout
      feature="advisor_network"
      pageTitle="Plan Selection"
      workflowTitle=""
      onClose={() => navigate(flowState.returnToPath)}
      size="full"
    >
      <Box css={{ display: 'grid', gap: 'space.500', placeItems: 'center', maxWidth: '800px', margin: '0 auto' }}>
        <H1 variant="pageTitle">Choose the level of advice that fits your needs.</H1>
        <Box css={{ display: 'grid', gap: 'space.300', placeItems: 'center' }}>
          {willShowBanner && (
            <Banner variant="info" css={{ width: '100%' }} data-testid="banStatusBanner">
              {statusCopy}
            </Banner>
          )}

          <Box
            css={{
              display: 'grid',
              gridTemplateColumns: {
                base: '1fr 1fr',
                mdDown: '1fr',
              },
              gap: 'space.200',
              width: '100%',
            }}
          >
            {financialServiceOptions.map(option => (
              <FinancialServiceOptionCard
                key={option.id}
                financialServiceOption={option}
                isBAN={option.__typename === 'BettermentAdvisorNetworkOption'}
                goToNextStep={goToNextStep}
              />
            ))}
          </Box>
          <Text>
            Not sure which is right for you?{' '}
            <SambaAutoLink
              to={urlBuilder.appAdviceInitiationInterests.absolute()}
              onClick={() => {
                trackClick({ name: 'ConciergeOneTimeCall', flow: 'BAN' });
              }}
            >
              Schedule a call
            </SambaAutoLink>{' '}
            with our team to learn more about our advisors.
          </Text>
        </Box>
        <TransferSupport />
      </Box>
    </WorkflowLayout>
  );
};
